import { useState } from "react"
import AvatarStack from "@/components/AvatarStack/AvatarStack"
import CardFace from "@/components/CardFace/CardFace"
import CardPeek from "@/components/CardFace/CardPeek"
import EmptyState from "@/components/EmptyState/EmptyState"
import JobEditModal from "@/components/JobEditModal/JobEditModal"
import JobStatement from "@/components/JobStatement/JobStatement"
import Pill from "@/components/Pill/Pill"
import RoundHeading from "@/components/RoundHeading/RoundHeading"
import Spinner from "@/components/Spinner/Spinner"
import { CARDS_COPY } from "@/content/cardsRound"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { setJobCards } from "@/services/jobs"
import { useSessionStore } from "@/store/sessionStore"
import { peopleFor, selectJob, selectPicks, selectSession, tallyPicks } from "@/store/selectors"
import type { CardId } from "@/types"
import styles from "@/styles/pages/Cards.module.css"

/*
 * The shortlist: what this job actually keeps.
 *
 * Every nomination, most-picked first, and the facilitator decides. They may drop
 * a card four people chose and keep one nobody did — the room's picks are input,
 * not a vote, and the job has to be coherent rather than popular.
 *
 * `setJobCards` also clears the placements and deep-dive answers of anything
 * dropped, which is why the warning is on screen rather than in a comment: it is
 * reversible, but not silently.
 *
 * This is also the one place order IS stored. `job.cardIds` is the facilitator's
 * own arrangement of their own shortlist, written whole by one writer, so the
 * usual reason order is never persisted does not apply.
 */

interface CardsReviewProps {
  canDrive: boolean
  /** The round is being read back: the shortlist is shown, never changed. */
  readOnly: boolean
}

export default function CardsReview({ canDrive, readOnly }: CardsReviewProps) {
  const session = useSessionStore(selectSession)
  const job = useSessionStore(selectJob)
  const picks = useSessionStore(selectPicks)
  const participants = useSessionStore((s) => s.participants)
  const { write, isPending } = useFirestoreWrite()
  const [flipped, setFlipped] = useState<CardId | null>(null)
  const [editing, setEditing] = useState(false)

  if (!session || !job) return null

  // Curating is the facilitator's, and only while the room is actually here.
  const curating = canDrive && !readOnly
  const nominations = tallyPicks(picks)
  const kept = job.cardIds

  /*
   * `setJobCards` writes the whole array, so it is read-modify-write: two of
   * these landing at once would each start from the same `kept` and one would
   * lose. The pending key is per card so only the row being changed spins, but
   * `shortlisting` is what stops a second row being pressed meanwhile.
   */
  const shortlisting = nominations.some((nomination) => isPending(nomination.cardId))

  const toggle = (cardId: CardId) => {
    const next = kept.includes(cardId) ? kept.filter((id) => id !== cardId) : [...kept, cardId]
    write(() => setJobCards(session.id, job.id, next), cardId)
  }

  return (
    <div className={styles.review}>
      <RoundHeading
        title={CARDS_COPY.reviewHeading}
        objective={CARDS_COPY.reviewObjective}
        status={CARDS_COPY.shortlistCount(kept.length, nominations.length)}
      />

      <JobStatement
        job={job}
        userStoryEnabled={session.config.userStoryEnabled}
        onEdit={curating ? () => setEditing(true) : undefined}
      />
      {editing ? (
        <JobEditModal
          sessionId={session.id}
          job={job}
          userStoryEnabled={session.config.userStoryEnabled}
          onClose={() => setEditing(false)}
        />
      ) : null}

      {curating && kept.length > 0 ? (
        <p className={styles.dropWarning}>{CARDS_COPY.dropWarning}</p>
      ) : null}

      {nominations.length === 0 ? (
        <EmptyState title={CARDS_COPY.emptyShortlist} />
      ) : (
        <>
          <p className={styles.reviewOrder}>{CARDS_COPY.reviewOrder}</p>
          <ol className={styles.nominations}>
            {nominations.map((nomination, index) => {
              const isKept = kept.includes(nomination.cardId)
              return (
                <li
                  key={nomination.cardId}
                  className={
                    isKept ? `${styles.nomination} ${styles.nominationKept}` : styles.nomination
                  }
                >
                  <span className={styles.rank}>{index + 1}</span>

                  {/* The thumbnail clamps the statement to four lines of 11px
                      type, and its own flip only shows the back at the same
                      size — the peek is how the facilitator actually reads a
                      nomination before deciding it. */}
                  <CardPeek cardId={nomination.cardId}>
                    <CardFace
                      cardId={nomination.cardId}
                      side={flipped === nomination.cardId ? "back" : "front"}
                      size="sm"
                      onFlip={() =>
                        setFlipped((current) =>
                          current === nomination.cardId ? null : nomination.cardId
                        )
                      }
                    />
                  </CardPeek>

                  <span className={styles.nominationMeta}>
                    <AvatarStack
                      people={peopleFor(participants, nomination.pickedBy)}
                      size={28}
                      max={5}
                    />
                    <span className={styles.nominationCount}>
                      {CARDS_COPY.nominatedBy(nomination.count)}
                    </span>
                    {isKept ? <Pill tone="done">{CARDS_COPY.kept}</Pill> : null}
                  </span>

                  {curating ? (
                    <button
                      type="button"
                      className={isKept ? `${styles.keep} ${styles.keepOn}` : styles.keep}
                      aria-pressed={isKept}
                      disabled={shortlisting}
                      aria-busy={isPending(nomination.cardId) || undefined}
                      onClick={() => toggle(nomination.cardId)}
                    >
                      {isPending(nomination.cardId) ? <Spinner size="sm" /> : null}
                      {isKept ? CARDS_COPY.dropCard : CARDS_COPY.keepCard}
                    </button>
                  ) : null}
                </li>
              )
            })}
          </ol>
          {/* Why six, said once, where the decision is being made. */}
          <p className={styles.reviewFoot}>{CARDS_COPY.reviewFoot}</p>
        </>
      )}
    </div>
  )
}
